/**
 * Templates list page.
 *
 * Displays all checklist templates in a FilterableDataTable with status badges,
 * filtering by version status, and quick actions (Duplicate, Archive, Publish).
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 5.7
 */

import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { GridColDef } from '@mui/x-data-grid';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArchiveIcon from '@mui/icons-material/Archive';
import PublishIcon from '@mui/icons-material/Publish';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TemplatePreviewDialog from './TemplatePreviewDialog';

import {
  FilterableDataTable,
  StatusBadge,
  PageContainer,
  type FilterConfig,
  type ActiveFilter,
  type RowAction,
  type TemplateVersionStatus,
} from '../../components/shared';
import { ChecklistTemplateControllerApi, MachineControllerApi } from '../../api-generated/apis';
import type { ChecklistTemplate } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';

const templateApi = new ChecklistTemplateControllerApi(apiConfig);
const machineApi = new MachineControllerApi(apiConfig);

// ─── Types ───────────────────────────────────────────────────────────────────

interface TemplateRow {
  id: string;
  name: string;
  description: string;
  status: TemplateVersionStatus;
  category: string;
  sectionCount: number;
  machineName: string;
  lastModified: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Maps a raw API template to a TemplateRow for the data table.
 * Since there's no version status endpoint, we default status to DRAFT.
 */
function mapTemplateToRow(template: ChecklistTemplate, machineMap: Map<string, string>): TemplateRow {
  const machineId = (template as any).machineId;
  return {
    id: template.id ?? '',
    name: template.name ?? '',
    description: template.description ?? '',
    status: (template.status as TemplateVersionStatus) ?? ('DRAFT' as TemplateVersionStatus),
    category: template.category?.name ?? '',
    sectionCount: template.sections?.length ?? 0,
    machineName: machineId ? (machineMap.get(machineId) ?? '—') : '—',
    lastModified: template.updatedAt?.toISOString() ?? template.createdAt?.toISOString() ?? '',
  };
}

/**
 * Formats an ISO date string to a readable locale date.
 */
function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '—';
  }
}

// ─── Column Definitions ──────────────────────────────────────────────────────

const columns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    flex: 2,
    minWidth: 200,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 140,
    renderCell: (params) => (
      <StatusBadge status={params.value as TemplateVersionStatus} size="medium" />
    ),
  },
  {
    field: 'sectionCount',
    headerName: 'Sections',
    width: 100,
    type: 'number',
  },
  {
    field: 'machineName',
    headerName: 'Machine',
    width: 150,
  },
  {
    field: 'lastModified',
    headerName: 'Last Modified',
    width: 150,
    renderCell: (params) => formatDate(params.value as string),
  },
];

// ─── Filter Configuration ────────────────────────────────────────────────────

const STATUS_FILTER: FilterConfig = {
  field: 'status',
  label: 'Status',
  type: 'select',
  options: [
    { value: 'DRAFT', label: 'Draft' },
    { value: 'PUBLISHED', label: 'Published' },
    { value: 'ARCHIVED', label: 'Archived' },
  ],
};

// ─── Component ───────────────────────────────────────────────────────────────

const TemplatesPage: React.FC = () => {
  const navigate = useNavigate();

  // Pagination state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  // Filter state
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);
  const [previewTemplate, setPreviewTemplate] = useState<ChecklistTemplate | null>(null);

  // Fetch templates via TanStack Query
  const { data, isLoading } = useQuery<ChecklistTemplate[]>({
    queryKey: ['templates', 'list', page, pageSize, activeFilters],
    queryFn: () => templateApi.getAllTemplates({}),
  });

  const { data: machinesData } = useQuery({
    queryKey: ['machines-list'],
    queryFn: () => machineApi.getAllMachines(),
    staleTime: 60_000,
  });

  const machineMap = useMemo(() => {
    const map = new Map<string, string>();
    (machinesData ?? []).forEach((m: any) => map.set(m.id, m.name));
    return map;
  }, [machinesData]);

  const filterConfigs: FilterConfig[] = useMemo(() => [
    STATUS_FILTER,
  ], []);

  // Map API data to table rows
  const allRows: TemplateRow[] = (data ?? []).map((t: ChecklistTemplate) => mapTemplateToRow(t, machineMap));

  // Apply filters client-side
  const statusFilter = activeFilters.find((f) => f.field === 'status');
  const rows = allRows.filter((row) => {
    if (statusFilter?.value && row.status !== statusFilter.value) return false;
    return true;
  });

  const totalCount = rows.length;

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handleRowClick = useCallback(
    (row: TemplateRow) => {
      navigate(`/templates/${row.id}/edit`);
    },
    [navigate],
  );

  const handleNewTemplate = useCallback(() => {
    navigate('/templates/new/edit');
  }, [navigate]);

  const handleFilterChange = useCallback((filters: ActiveFilter[]) => {
    setActiveFilters(filters);
  }, []);

  const handleDuplicate = useCallback((row: TemplateRow) => {
    // Placeholder: duplicate action
    console.log('Duplicate template:', row.id);
  }, []);

  const handleArchive = useCallback((row: TemplateRow) => {
    // Placeholder: archive action
    console.log('Archive template:', row.id);
  }, []);

  const handlePublish = useCallback((row: TemplateRow) => {
    // Placeholder: publish action
    console.log('Publish template:', row.id);
  }, []);

  const handlePreview = useCallback((row: TemplateRow) => {
    const template = (data ?? []).find((t: any) => (t.id ?? '') === row.id);
    if (template) setPreviewTemplate(template);
  }, [data]);

  // ─── Row Actions ───────────────────────────────────────────────────────

  const rowActions: RowAction<TemplateRow>[] = [
    {
      label: 'Preview',
      icon: VisibilityIcon,
      onClick: handlePreview,
    },
    {
      label: 'Duplicate',
      icon: ContentCopyIcon,
      onClick: handleDuplicate,
    },
    {
      label: 'Archive',
      icon: ArchiveIcon,
      onClick: handleArchive,
      show: (row) => row.status === 'PUBLISHED',
    },
    {
      label: 'Publish',
      icon: PublishIcon,
      onClick: handlePublish,
      show: (row) => row.status === 'DRAFT',
    },
  ];

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <PageContainer title="Templates" subtitle="Manage checklist templates">
      <FilterableDataTable<TemplateRow>
        rows={rows}
        columns={columns}
        loading={isLoading}
        totalCount={totalCount}
        searchPlaceholder="Search templates…"
        filters={filterConfigs}
        onFilterChange={handleFilterChange}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onRowClick={handleRowClick}
        actions={rowActions}
        primaryAction={{ label: 'New Template', onClick: handleNewTemplate }}
      />
      <TemplatePreviewDialog
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        templateName={previewTemplate?.name ?? ''}
        sections={(previewTemplate?.sections ?? []).flatMap((s: any) =>
          (s.items ?? []).map((item: any) => ({
            question: item.question ?? s.name ?? '',
            sectionType: item.type ?? 'STATUS_CHECK',
            machinePartName: item.machinePartName ?? s.machinePartName,
            minValue: item.minValue,
            maxValue: item.maxValue,
            warningMin: item.warningMin,
            warningMax: item.warningMax,
          }))
        )}
      />
    </PageContainer>
  );
};

export default TemplatesPage;
